import { useMemo, type ComponentType } from 'react';
import Select from 'react-select';
import { getCountryCallingCode, type Country } from 'react-phone-number-input';
import * as flagIcons from 'country-flag-icons/react/3x2';

// react-phone-number-input renders its own native country <select>, which only
// shows country names and can't be searched beyond first-letter type-ahead. This
// is a drop-in `countrySelectComponent` replacement built on react-select so the
// list is searchable, shows the dial code ("Norway (+47)"), and reuses the dkn-rs
// theme (dark mode for free). It receives the props PhoneInput passes to a country
// select: value (ISO code), options [{value, label, divider?}], onChange(code), etc.

const flagMap = flagIcons as unknown as Record<string, ComponentType<{ className?: string; title?: string }>>;

interface RpniCountryOption {
    value?: string; // ISO country code, or undefined for the "International" entry
    label: string; // localized country name
    divider?: boolean;
}

interface PhoneCountrySelectProps {
    name?: string;
    value?: string;
    options: RpniCountryOption[];
    onChange: (value?: string) => void;
    onFocus?: () => void;
    onBlur?: () => void;
    disabled?: boolean;
    readOnly?: boolean;
    'aria-label'?: string;
}

interface CountryOption {
    value: string; // ISO code
    country: string; // ISO code (for the flag)
    dial: string; // "+47" — compact label for the closed control
    label: string; // "Norway (+47)" — used for search/filter + a11y
}

export function PhoneCountrySelect({
    name,
    value,
    options,
    onChange,
    onFocus,
    onBlur,
    disabled,
    readOnly,
    'aria-label': ariaLabel,
}: PhoneCountrySelectProps) {
    const countryOptions = useMemo<CountryOption[]>(
        () =>
            options
                // Drop the "International" entry (no country) and visual dividers.
                .filter((o): o is RpniCountryOption & { value: string } => !!o.value && !o.divider)
                .map((o) => {
                    let dial = '';
                    try {
                        dial = `+${getCountryCallingCode(o.value as Country)}`;
                    } catch {
                        // Country without a calling code in the metadata — leave it blank.
                    }
                    return {
                        value: o.value,
                        country: o.value,
                        dial,
                        label: dial ? `${o.label} (${dial})` : o.label,
                    };
                }),
        [options],
    );

    const selected = countryOptions.find((o) => o.value === value) ?? null;

    return (
        <Select<CountryOption, false>
            name={name}
            aria-label={ariaLabel}
            className="dkn-phone__country"
            classNamePrefix="dkn-rs"
            isDisabled={disabled || readOnly}
            options={countryOptions}
            value={selected}
            onChange={(opt) => onChange(opt?.value)}
            onFocus={onFocus}
            onBlur={onBlur}
            placeholder=""
            // The closed control stays compact (flag + "+47"); the open menu shows
            // the full "Norway (+47)". Filtering uses the option's `label`, so typing
            // either "nor" or "47" finds Norway.
            formatOptionLabel={(o, meta) => {
                const Flag = flagMap[o.country];
                return (
                    <span className="dkn-phone__opt">
                        {Flag ? <Flag className="dkn-phone__flag" title={o.country} /> : null}
                        <span>{meta.context === 'menu' ? o.label : o.dial || o.label}</span>
                    </span>
                );
            }}
        />
    );
}
